import './assets/main.css'
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'
import { createI18n } from 'vue-i18n'
import PrimeVue from 'primevue/config'
import Tooltip from 'primevue/tooltip'
import 'primeicons/primeicons.css'
import App from './App.vue'
import router from './router'
import en from './locales/en.json'
import ar from './locales/ar.json'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import { VueReCaptcha } from 'vue-recaptcha-v3'
import { MyPreset } from './config/theme'

const messages = {
  en,
  ar,
}

const app = createApp(App)

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: true, // Enable tab-switch refetching
      refetchOnReconnect: true, // Also refetch on network reconnect
    },
  },
})

// Get stored language preference or default to 'en'
const getStoredLanguage = (): string => {
  const storedLanguage = localStorage.getItem('selectedLanguage')
  if (storedLanguage) {
    try {
      const parsedLanguage = JSON.parse(storedLanguage)
      if (parsedLanguage && parsedLanguage.value) {
        return parsedLanguage.value
      }
    } catch (err) {
      console.error('Error parsing stored language:', err)
    }
  }
  return 'en'
}

const storedLocale = getStoredLanguage()

const i18n = createI18n({
  locale: storedLocale, // set locale from storage or default
  fallbackLocale: 'en', // set fallback locale
  messages, // set locale messages
})
app.use(ToastService)
app.use(ConfirmationService)
app.directive('tooltip', Tooltip)
app.use(createPinia())
app.use(router)
app.use(VueQueryPlugin, { queryClient })
app.use(PrimeVue, {
  theme: {
    preset: MyPreset,
    options: {
      darkModeSelector: false,
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue',
      },
    },
  },
})
app.use(i18n)
app.use(VueReCaptcha, {
  siteKey: '6LemS6ArAAAAABLifVowtwRCunDZWnM_1c51Baxk',
  loaderOptions: {
    useRecaptchaNet: false,
    autoHideBadge: true,
  },
})

async function initializeApp() {
  const storedLanguage = localStorage.getItem('selectedLanguage')
  if (storedLanguage) {
    try {
      const parsedLanguage = JSON.parse(storedLanguage)
      if (parsedLanguage && parsedLanguage.direction) {
        document.documentElement.dir = parsedLanguage.direction
        document.documentElement.lang = parsedLanguage.value
      }
    } catch (err) {
      console.error('Error setting document direction:', err)
    }
  }

  app.mount('#app')
}

// Initialize the app
initializeApp()
